import { storyThemeIdOf } from './storyFrames';
import { storyAlpha, type StoryColours, type StoryCss } from './storyStyles';

/**
 * The cover frame of each release theme on a kit card (the CSS of `BannerFrame`, `ThumbnailFrame`
 * and `LinkPreviewFrame` of the asset redesign). The frame is the frame of the Story (card, sticker,
 * soft card, plate, arch, sleeve, tube, photo); each card gives its own numbers, in px of the file.
 */
export interface CardCoverSpec {
  clean: { radius: number; shadow: [number, number, number] };
  hype: { turn: number; border: number; shadow: number };
  dreamy: { radius: number; glow: [number, number, number] };
  dark: { pad: number; outline: number; grey: number };
  warm: { radius: number; shadow: [number, number, number] };
  retro: {
    border: number;
    shadow: [number, number, number];
    record: { top: string; right: string; width: string; inset: number } | null;
  };
  neon: { pad: number; border: number; radius: number; glow: number; inset: number; pictureRadius: number };
  handmade: { pad: [number, number, number]; turn: number; shadow: [number, number, number]; tape: [number, number] };
}

export interface CardCoverFrame {
  outer: StoryCss;
  picture: StoryCss;
  tape: StoryCss | null;
  record: StoryCss | null;
  /** The height of the outer box for an outer width (the Handmade mat is taller than wide). */
  heightOf: (width: number) => number;
}

const square = (width: number): number => width;
const shadowCss = ([y, blur, spread]: [number, number, number], colour: string): string =>
  `0 ${y}px ${blur}px ${spread}px ${colour}`;

/** The cover frame of a theme on a card with the numbers of `spec`. An unknown theme is Clean. Pure. */
export function cardCoverFrame(themeId: string, colors: StoryColours, spec: CardCoverSpec): CardCoverFrame {
  const id = storyThemeIdOf(themeId);
  switch (id) {
    case 'clean':
      return {
        outer: {},
        picture: { borderRadius: spec.clean.radius, boxShadow: shadowCss(spec.clean.shadow, 'rgba(0,0,0,.45)') },
        tape: null,
        record: null,
        heightOf: square,
      };
    case 'hype':
      return {
        outer: { transform: `rotate(${spec.hype.turn}deg)` },
        picture: {
          border: `${spec.hype.border}px solid ${colors.accent}`,
          boxShadow: `${spec.hype.shadow}px ${spec.hype.shadow}px 0 ${colors.surface}`,
        },
        tape: null,
        record: null,
        heightOf: square,
      };
    case 'dreamy':
      return {
        outer: {},
        picture: {
          borderRadius: spec.dreamy.radius,
          boxShadow: shadowCss(spec.dreamy.glow, storyAlpha(colors.accent, 0.7)),
        },
        tape: null,
        record: null,
        heightOf: square,
      };
    case 'dark':
      return {
        outer: {
          padding: spec.dark.pad,
          background: colors.surface,
          outline: `${spec.dark.outline}px solid ${storyAlpha(colors.text, 0.22)}`,
        },
        picture: { filter: `grayscale(${spec.dark.grey}) contrast(1.05)` },
        tape: null,
        record: null,
        heightOf: square,
      };
    case 'warm':
      return {
        outer: {},
        picture: {
          borderRadius: `999px 999px ${spec.warm.radius}px ${spec.warm.radius}px`,
          boxShadow: shadowCss(spec.warm.shadow, 'rgba(0,0,0,.6)'),
        },
        tape: null,
        record: null,
        heightOf: square,
      };
    case 'retro': {
      const { record } = spec.retro;
      return {
        outer: {},
        picture: {
          border: `${spec.retro.border}px solid ${colors.text}`,
          boxShadow: shadowCss(spec.retro.shadow, 'rgba(0,0,0,.6)'),
        },
        tape: null,
        record:
          record === null
            ? null
            : {
                position: 'absolute',
                zIndex: 0,
                top: record.top,
                right: record.right,
                width: record.width,
                aspectRatio: '1 / 1',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${colors.text} 0 9%, ${colors.ink} 9% 16%, #1a1214 16.5% 100%)`,
                boxShadow: `inset 0 0 0 ${record.inset}px #221a1c`,
              },
        heightOf: square,
      };
    }
    case 'neon':
      return {
        outer: {
          padding: spec.neon.pad,
          border: `${spec.neon.border}px solid ${colors.accent}`,
          borderRadius: spec.neon.radius,
          boxShadow: `0 0 ${spec.neon.glow}px ${colors.accent}, inset 0 0 ${spec.neon.inset}px ${storyAlpha(colors.accent, 0.6)}`,
        },
        picture: { borderRadius: spec.neon.pictureRadius },
        tape: null,
        record: null,
        heightOf: square,
      };
    case 'handmade': {
      const [top, side, bottom] = spec.handmade.pad;
      const [tapeTop, tapeHeight] = spec.handmade.tape;
      return {
        outer: {
          background: '#fffdf8',
          padding: `${top}px ${side}px ${bottom}px`,
          transform: `rotate(${spec.handmade.turn}deg)`,
          boxShadow: shadowCss(spec.handmade.shadow, 'rgba(0,0,0,.45)'),
        },
        picture: {},
        tape: {
          position: 'absolute',
          zIndex: 2,
          top: tapeTop,
          left: '30%',
          width: '40%',
          height: tapeHeight,
          background: storyAlpha(colors.surface, 0.75),
          transform: 'rotate(3deg)',
        },
        record: null,
        // The photo is the width less the two side paddings; the mat adds the top and the bottom.
        heightOf: (width) => width - 2 * side + top + bottom,
      };
    }
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}
